import { reactive } from "vue";
import { diff } from "../lib/diff";
import { game, resetGame } from "./init";

const SAVE_SLOT_COUNT = 5;
const ACTIVE_SLOT_KEY = "shade-survival-active-slot";
const SLOT_KEY_PREFIX = "shade-survival-save-slot-";
const AUTOSAVE_INTERVAL_TICKS = 1000;

let lastAutosavedTick = -AUTOSAVE_INTERVAL_TICKS;

type SaveSlot = {
  id: number;
  hasSave: boolean;
};

export const saveSlotsState = reactive({
  activeSlot: 1,
  slots: Array.from({ length: SAVE_SLOT_COUNT }, (_, index) => ({
    id: index + 1,
    hasSave: false,
  })) as SaveSlot[],
});

export function initializeSaveSlots() {
  saveSlotsState.activeSlot = readActiveSlot();
  refreshSaveSlots();
  loadActiveSlot();
}

export function saveActiveSlot() {
  saveSlot(saveSlotsState.activeSlot);
  lastAutosavedTick = game.state.tick;
}

export function autosaveActiveSlot() {
  if (game.state.tick - lastAutosavedTick < AUTOSAVE_INTERVAL_TICKS) return;
  saveActiveSlot();
}

export function switchSaveSlot(slot: number) {
  const nextSlot = normalizeSlot(slot);
  if (nextSlot === saveSlotsState.activeSlot) return;

  saveActiveSlot();
  saveSlotsState.activeSlot = nextSlot;
  localStorage.setItem(ACTIVE_SLOT_KEY, String(nextSlot));
  loadActiveSlot();
}

export function resetActiveSlot() {
  resetGame();
  saveSlot(saveSlotsState.activeSlot);
}

export function loadActiveSlot() {
  const saved = localStorage.getItem(slotKey(saveSlotsState.activeSlot));
  if (!saved) {
    resetGame();
    refreshSaveSlots();
    return;
  }

  const saveData = JSON.parse(saved);
  game.loadState(saveData);

  const reSerialized = game.saveState();
  const d = diff(saveData, reSerialized);
  if (d !== undefined) {
    console.error("Serialization mismatch!", d);
  }

  refreshSaveSlots();
}

function saveSlot(slot: number) {
  const saveData = game.saveState();
  localStorage.setItem(slotKey(slot), JSON.stringify(saveData));
  refreshSaveSlots();
}

function refreshSaveSlots() {
  for (const slot of saveSlotsState.slots) {
    slot.hasSave = localStorage.getItem(slotKey(slot.id)) !== null;
  }
}

function readActiveSlot() {
  return normalizeSlot(Number(localStorage.getItem(ACTIVE_SLOT_KEY) ?? 1));
}

function normalizeSlot(slot: number) {
  return Number.isInteger(slot) && slot >= 1 && slot <= SAVE_SLOT_COUNT ? slot : 1;
}

function slotKey(slot: number) {
  return `${SLOT_KEY_PREFIX}${slot}`;
}
